import React from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Navigation } from "./components/Navigation";
import { ReportHistory } from "./components/ReportHistory";
import { OverviewPage } from "./pages/OverviewPage";
import { RetailerPage } from "./pages/RetailerPage";
import { UploadPage } from "./pages/UploadPage";
import {
  UploadResultsPage,
  UploadResultsPageWithState,
} from "./pages/UploadResultsPage";
import { ExportTemplatePage } from "./pages/ExportTemplatePage";
import { ArtifactExportPage } from "./pages/ArtifactExportPage";
import { HessSkuYoYPage } from "./pages/HessSkuYoYPage";
import { ResersQ1DashboardPage } from "./pages/ResersQ1DashboardPage";
import { PrivacyPage } from "./pages/PrivacyPage";
import { TermsPage } from "./pages/TermsPage";
import { exportDashboardScreenshot } from "./utils/exportUtils";
import { log } from "./utils/logger";
import { getAllReports, getReport } from "./utils/reportManager";
import { useTeamsTheme } from "./hooks/useTeamsTheme";

/**
 * The top-level App component orchestrates multi-retailer navigation and routing.
 * Manages global state (dark mode, export functionality) and provides routing
 * between overview and individual retailer dashboard pages.
 *
 * Features:
 * - Multi-retailer navigation with React Router
 * - Global dark/light mode toggle
 * - Export dashboard functionality across all pages
 * - Responsive layout for desktop and mobile
 * - Backward compatibility with existing Walmart dashboard
 */
const App: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Track dark mode state globally. When toggled, the `dark` class is
  // applied to the document root, causing Tailwind's dark mode styles to
  // activate across all pages. In a real application you might persist this to localStorage.
  const [darkMode, setDarkMode] = React.useState(false);

  // Sync with Microsoft Teams theme when running inside Teams
  const { isInTeams } = useTeamsTheme(setDarkMode);

  React.useEffect(() => {
    const root = document.documentElement;
    if (darkMode) {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, [darkMode]);

  // Export functionality state
  const [isExporting, setIsExporting] = React.useState(false);
  const [exportError, setExportError] = React.useState<string | null>(null);

  // Report history state
  const [isHistoryOpen, setIsHistoryOpen] = React.useState(false);
  const [reportCount, setReportCount] = React.useState(0);

  // View mode state (for results page)
  const [viewMode, setViewMode] = React.useState<
    "original" | "slide" | "compact"
  >("original");

  // Update report count periodically
  React.useEffect(() => {
    const updateCount = () => setReportCount(getAllReports().length);
    updateCount();
    const interval = setInterval(updateCount, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleToggleDarkMode = () => {
    setDarkMode((prev) => !prev);
  };

  const handleExportScreenshot = async () => {
    setIsExporting(true);
    setExportError(null);
    try {
      await exportDashboardScreenshot();
      log.info("Dashboard export completed successfully", {}, "ui");
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown export error";
      log.error("Export failed", error, "ui");
      setExportError(`Export failed: ${errorMessage}`);

      // Clear error after 5 seconds
      setTimeout(() => setExportError(null), 5000);
    } finally {
      setIsExporting(false);
    }
  };

  const handleClearExportError = () => {
    setExportError(null);
  };

  const handleReportSelect = (reportId: string) => {
    navigate(`/upload/results?id=${reportId}`);
    setIsHistoryOpen(false);
  };

  const handleViewModeChange = (mode: "original" | "slide" | "compact") => {
    setViewMode(mode);
  };

  // Determine if we're on the results page
  const isResultsPage = location.pathname === "/upload/results";
  const isArtifactExportPage = location.pathname === "/artifact-export";

  return (
    <div
      className={
        isArtifactExportPage
          ? "min-h-screen bg-transparent"
          : "min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors"
      }
    >
      {/* Global Navigation */}
      {!isArtifactExportPage && (
        <Navigation
          darkMode={darkMode}
          onToggleDarkMode={handleToggleDarkMode}
          onExportScreenshot={handleExportScreenshot}
          isExporting={isExporting}
          exportError={exportError}
          onClearExportError={handleClearExportError}
          showReportHistory={true}
          reportHistoryCount={reportCount}
          onOpenReportHistory={() => setIsHistoryOpen(true)}
          viewMode={isResultsPage ? viewMode : undefined}
          onViewModeChange={isResultsPage ? handleViewModeChange : undefined}
        />
      )}

      {/* Global Report History Sidebar */}
      {!isArtifactExportPage && (
        <ReportHistory
          currentReportId={undefined}
          onReportSelect={handleReportSelect}
          isOpen={isHistoryOpen}
          onToggle={() => setIsHistoryOpen(!isHistoryOpen)}
        />
      )}

      {/* Main Content Routes */}
      <main>
        <Routes>
          {/* Redirect root to upload page (client-facing entry point) */}
          <Route path="/" element={<Navigate to="/upload" replace />} />

          {/* Campaign report upload pages */}
          <Route path="/upload" element={<UploadPage />} />
          <Route
            path="/upload/results"
            element={
              <UploadResultsPageWithState
                viewMode={viewMode}
                onViewModeChange={handleViewModeChange}
              />
            }
          />

          {/* Multi-retailer overview page */}
          <Route path="/overview" element={<OverviewPage />} />

          {/* Individual retailer dashboard pages */}
          <Route path="/walmart" element={<RetailerPage />} />
          <Route path="/albertsons" element={<RetailerPage />} />
          <Route path="/kroger" element={<RetailerPage />} />
          <Route path="/instacart" element={<RetailerPage />} />

          {/* Widescreen export route */}
          <Route path="/export/:templateId?" element={<ExportTemplatePage />} />
          <Route path="/artifact-export" element={<ArtifactExportPage />} />

          {/* Static Hess all-SKU YoY impact dashboard */}
          <Route path="/hess-sku-yoy" element={<HessSkuYoYPage />} />

          {/* Static Reser's Q1 brand/retailer dashboard */}
          <Route path="/resers-q1" element={<ResersQ1DashboardPage />} />

          {/* Legal pages (required for Teams app manifest) */}
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />

          {/* Dynamic retailer route (fallback for any other retailer) */}
          <Route path="/:retailerId" element={<RetailerPage />} />

          {/* Catch-all redirect to upload */}
          <Route path="*" element={<Navigate to="/upload" replace />} />
        </Routes>
      </main>
    </div>
  );
};

export default App;
